import { FC, useState } from 'react'
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  SortingState,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  ColumnDef,
  CellContext,
} from '@tanstack/react-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDownAZ, faArrowUpAZ } from '@fortawesome/pro-regular-svg-icons'
import classNames from 'classnames'
import { TableFilterField } from 'components/tables/TableFilterField'
import { WorkdayEmployeeUpdateFailure } from '../../types'

interface Props {
  failures: WorkdayEmployeeUpdateFailure[]
}

const columnHelper = createColumnHelper<WorkdayEmployeeUpdateFailure>()

export const Table: FC<Props> = ({ failures }) => {
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'receivedAt',
      desc: true,
    },
  ])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const columns = [
    columnHelper.accessor('receivedAt', {
      header: 'Received',
    }),
    columnHelper.accessor('workerId', {
      header: 'Worker ID',
      filterFn: 'includesString',
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      filterFn: 'includesString',
    }),
    {
      id: 'attributes',
      header: 'Attributes',
      filterFn: 'includesString',
      accessorFn: (failure: WorkdayEmployeeUpdateFailure) =>
        JSON.stringify(failure.updateAttributes, null, 2),
      cell: ({
        getValue,
      }: CellContext<WorkdayEmployeeUpdateFailure, string>) => (
        <details>
          <summary>Attributes</summary>
          <pre className="text-sm">{getValue()}</pre>
        </details>
      ),
    } as ColumnDef<WorkdayEmployeeUpdateFailure>,
    columnHelper.accessor('errorMessage', {
      filterFn: 'includesString',
      header: 'Error Message',
    }),
  ]

  const table = useReactTable({
    data: failures,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
  })

  return (
    <div className="px-8 pt-6 flex flex-col flex-grow">
      <div className="relative flex-grow">
        <div className="absolute top-0 bottom-0 left-0 right-0 overflow-auto">
          <table className="text-sm min-w-full">
            <thead className="sticky top-0 shadow z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="p-4 px-4 py-6 font-semibold text-left border border-t-0 cursor-pointer bg-neutral-100 text-neutral-900 border-neutral-300 hover:bg-neutral-200 first-of-type:border-l-0 last-of-type:border-r-0"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex flex-col gap-y-2">
                        <div className="flex justify-between">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}

                          {header.column.getIsSorted() && (
                            <FontAwesomeIcon
                              icon={
                                header.column.getIsSorted() === 'asc'
                                  ? faArrowDownAZ
                                  : faArrowUpAZ
                              }
                            />
                          )}
                        </div>

                        {header.column.getCanFilter() && (
                          <div className="max-w-sm">
                            <TableFilterField column={header.column} />
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={classNames('group/row')}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-5 border border-neutral-300 first-of-type:border-l-0 last-of-type:border-r-0"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
